import Image from 'next/image'
import Link from 'next/link'
import { useEffect, type FC } from 'react'

import { Card } from '@wartek-id/card'
import { Text } from '@wartek-id/text-v1'
import { Badge } from '@wartek-id/badge'
import { Button } from '@wartek-id/button-v1'

import {
  Banner,
  BannerTitle,
  BannerDescription,
} from 'app/KinerjaShared/components/Banner'
import { PENGELOLAAN_KINERJA_PS_PATH } from 'app/KinerjaShared/constants/path'
import KinerjaSidebar from 'app/KinerjaShared/components/KinerjaSidebar'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import { useKinerjaPsTrackEvent } from 'app/KinerjaPSPage/hooks/useKinerjaPsTrackEvent'
import CardPredikatByDinas from 'app/KinerjaPSPage/components/CardPredikatByDinas'
import { BannerGantiPeriode } from 'app/KinerjaShared/components/BannerGantiPeriode'
import CardPeriodType from 'app/KinerjaShared/components/CardPeriodType'
import CardPerencanaanPending from 'app/KinerjaPSPage/components/CardPerencanaanPending'
import Before2025 from 'app/KinerjaShared/components/Before2025'
import { BannerUpdatedTargetPerilaku } from 'app/KinerjaPSPage/components/BannerUpdatedTargetPerilaku'
import CardPenilaianPeriodik from 'app/KinerjaShared/components/CardPenilaianPeriodik'
import useToggleSdkContext from 'utils/useToogleSdk'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { useApiGetPenilaianPeriodikTypePS } from 'api/kinerja/pengawas-sekolah/penilaian-periodik'
import { PengelolaanKinerjaTimeline } from 'app/KinerjaShared/components/PengelolaanKinerjaTimeline'
import CardKinerjaSteps from 'app/KinerjaShared/components/CardKinerjaSteps'
import { useKinerjaTipe } from 'app/KinerjaShared/hooks/useKinerjaTipe'

interface KinerjaPsIndexProps {}

const KinerjaPsIndex: FC<KinerjaPsIndexProps> = () => {
  const { periodYear } = usePeriodContext()
  const toggleValue = useToggleSdkContext()
  const isPenilaianPeriodikEnabled =
    toggleValue?.['kinerja-penilaian-periodik-enabled'] &&
    toggleValue?.['kinerja-ps-penilaian-periodik-ks-enabled'] &&
    periodYear >= 2025
  const { tipe } = useKinerjaTipe()

  const {
    isLoading: isLoadingRole,
    isPengawasSekolah,
    leave,
  } = useRoleContext()
  if (!isLoadingRole && !isPengawasSekolah) leave()

  const { data: periodicTypeData, isSuccess: isSuccessPeriodicType } =
    useApiGetPenilaianPeriodikTypePS({
      params: { skpYear: periodYear, tipe },
      options: {
        enabled: isPenilaianPeriodikEnabled,
        staleTime: 60 * 60 * 1000,
        cacheTime: 60 * 60 * 1000,
      },
    })

  const { trackOpenKinerjaPenilaianHomepage } = useKinerjaPsTrackEvent()

  useEffect(() => {
    trackOpenKinerjaPenilaianHomepage('KINERJA_KS_PENILAI_HOMEPAGE')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="pl-64">
      <KinerjaSidebar sticky activeMenu="ps-penilai" />
      <div className="p-6 flex flex-col gap-6 w-full">
        <BannerGantiPeriode />

        <BannerUpdatedTargetPerilaku />

        <CardPerencanaanPending />

        <Card>
          <div className="flex gap-6 p-6 items-center">
            <div className="flex flex-col">
              <Image
                alt=""
                src="/illustrations/img-book-rapor-graphic.png"
                width={84}
                height={84}
              />
            </div>
            <div className="flex-1 flex flex-col gap-1 justify-center">
              <Before2025>
                <Text
                  variant="subheading"
                  color="informational"
                  className="!text-blue-60 !uppercase tracking-[0.5px]"
                >
                  <CardPeriodType withDate type="pelaksanaan" />
                </Text>
              </Before2025>
              <Before2025
                childrenFallback={
                  <>
                    <div className="flex items-center gap-2">
                      <Text variant="heading-lg">
                        Pemantauan Kinerja dan Rekomendasi Predikat Kinerja
                      </Text>
                    </div>
                    <Text color="subdued">
                      Lakukan untuk seluruh Kepala Sekolah di wilayah binaan
                      Anda.
                    </Text>
                  </>
                }
              >
                <div className="flex items-center gap-2">
                  <Text variant="heading-lg">Penilaian Kinerja</Text>
                  <Badge className="uppercase">Khusus Pengawas Sekolah</Badge>
                </div>
                <Text color="subdued">
                  Kumpulkan dokumen dan berikan nilai untuk seluruh kepala
                  sekolah di wilayah Anda.
                </Text>
              </Before2025>
            </div>

            <div className="px-6">
              <Link href={`${PENGELOLAAN_KINERJA_PS_PATH}/beranda`} passHref>
                <Button as="a" size="lg" color="black" className="w-60">
                  Lakukan
                </Button>
              </Link>
            </div>
          </div>
        </Card>

        <Banner variant="info" dismissible>
          <BannerTitle>
            Khusus Sekolah Swasta: Diskusikan Pengelolaan Kinerja dengan
            Pimpinan Badan Penyelenggara
          </BannerTitle>
          <BannerDescription>
            Sebagai Tim Kinerja dari Kepala Sekolah ASN di sekolah swasta, Anda
            dapat berdiskusi dengan Pimpinan Badan Penyelenggara (Yayasan,
            Perkumpulan, dsb) pada setiap tahap Pengelolaan Kinerja, agar
            seluruh proses berjalan sesuai kesepakatan bersama.
          </BannerDescription>
        </Banner>

        {isSuccessPeriodicType && (
          <CardPenilaianPeriodik
            periodicType={periodicTypeData.data.periodicType}
            href={`${PENGELOLAAN_KINERJA_PS_PATH}/penilaian/periodik`}
          />
        )}
        <CardPredikatByDinas />

        {periodYear < 2025 ? (
          <CardKinerjaSteps />
        ) : (
          <PengelolaanKinerjaTimeline
            timeline={[
              {
                title: 'Januari',
                steps: ['Perencanaan Kinerja'],
              },
              {
                title: 'Februari - November',
                steps: [
                  'Persiapan Praktik Kinerja',
                  'Pelaksanaan Observasi Praktik Kinerja',
                  'Pelaksanaan Tindak Lanjut Praktik Kinerja',
                ],
              },
              {
                title: 'Desember',
                steps: ['Rekomendasi Predikat Kinerja'],
              },
            ]}
          />
        )}
      </div>
    </div>
  )
}
export default KinerjaPsIndex
